import { useCanvasCalendarNav } from "@/components/canvas/useCanvasCalendarNav.ts";
import { CanvasHost, type CanvasHostProps } from "@/components/canvas/CanvasHost.tsx";
import {
  type CanvasCalendarSurface,
  type CanvasCalendarSurfaceHostProps,
  useCanvasCalendarSurface,
} from "@/components/canvas/useCanvasCalendarSurface.tsx";
import { SplitPanelFrame } from "@/components/layout/SplitPanel/SplitPanelFrame.tsx";
import { SplitPanelHeader } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { type ReactNode, useState } from "react";
import { CalendarHeader } from "@/components/calendars/CalendarHeader.tsx";
import { HeaderUtilitySlot } from "@/components/layout/HeaderUtilitySlot.tsx";
import {
  CalendarOptionsSheet,
  CalendarsDialog,
} from "@/components/calendars/CalendarOptionsSheet.tsx";
import { DayWeekStrip } from "@/components/calendars/DayWeekStrip.tsx";
import { MonthTitlePicker } from "@/components/calendars/MonthTitlePicker.tsx";

/** The per-route chrome slots the calendar panel exposes, so they can't drift
 *  between the panel, its frame, and the header: `leading` (home's app-nav +
 *  threads-toggle when the threads panel is hidden) and `accountSwitcher`
 *  (which hops between panels by visibility). */
type CanvasPanelChromeProps = {
  leading?: ReactNode;
  accountSwitcher?: ReactNode;
};

/**
 * The one calendar-panel header shared by home and the thread canvas — one
 * source of truth so the header can't visibly change on navigation. Reads the
 * shared nav (`useCanvasCalendarNav`) directly, so view/date and the
 * snap-to-today view-switch + scroll-to-now behavior are identical on both
 * routes. Surfaces slots for the bits that legitimately differ per route:
 * `leading` (home's app-nav + threads-toggle when the threads panel is hidden)
 * and `accountSwitcher` (which hops between panels by visibility).
 */
const CanvasCalendarHeader = ({ leading, accountSwitcher }: CanvasPanelChromeProps) => {
  const { currentDate, setCurrentDate, viewType, setViewType, scrollToNow } =
    useCanvasCalendarNav();
  // The calendar-options surface lives here, in the one shared header, so home
  // and the thread canvas offer it identically without each route wiring an
  // opener through the chrome props — mirroring native, where the sheet is
  // mounted once at the screen level. One flag drives both breakpoints' surfaces
  // because only one of them is ever mounted.
  const [optionsOpen, setOptionsOpen] = useState(false);
  // Drives which calendar-options surface mounts (sheet on a phone, dialog on
  // desktop). Derived here rather than accepted as a prop, so no route can
  // supply its own answer and drift from the other.
  const isMobile = useIsMobile();

  return (
    <>
      <SplitPanelHeader>
        {leading}
        <MonthTitlePicker currentDate={currentDate} onDateChange={setCurrentDate} />
        <CalendarHeader
          viewType={viewType}
          onViewTypeChange={setViewType}
          currentDate={currentDate}
          onDateChange={setCurrentDate}
          onGoToToday={scrollToNow}
          // Offered at every width; the header decides from its own container
          // which control opens it — the sliders icon where it is narrow, a
          // "Calendars" entry in the view picker where it is not. Desktop used
          // to reach visibility through the calendars aside instead.
          onOpenCalendarOptions={() => setOptionsOpen(true)}
        />
        {accountSwitcher}
        {/* Here rather than inside `CalendarHeader`, which is a cluster the
            preview screen drops into a site header that publishes for itself —
            two publishers on one screen. This is the header. */}
        <HeaderUtilitySlot />
      </SplitPanelHeader>
      {/* One surface per breakpoint, and only the one that can open is mounted:
          the visibility hook inside fetches the calendar list, so mounting both
          would pay for it twice. */}
      {isMobile ? (
        <CalendarOptionsSheet
          open={optionsOpen}
          onOpenChange={setOptionsOpen}
          viewType={viewType}
          onViewTypeChange={setViewType}
        />
      ) : (
        // Views as well as visibility: on a pane narrower than the header's
        // `@md`, this dialog is the only view switcher on screen.
        <CalendarsDialog
          open={optionsOpen}
          onOpenChange={setOptionsOpen}
          viewType={viewType}
          onViewTypeChange={setViewType}
        />
      )}
    </>
  );
};

const CanvasCalendarDayStrip = ({ visible = true }: { visible?: boolean }) => {
  const { currentDate, setCurrentDate, viewType } = useCanvasCalendarNav();

  if (!visible || (viewType !== "day" && viewType !== "multi-day")) return null;

  // iOS-style week strip — mobile/tablet, single-day + multi-day views. Sits
  // above the time grid and provides week-level navigation. Hidden at @5xl where
  // the full grid + MonthTitlePicker popover cover navigation. The strip's
  // selected day is the whole date story — no repeated day-title bar below it.
  return (
    <div className="@5xl:hidden">
      <DayWeekStrip
        currentDate={currentDate}
        visibleDayCount={viewType === "multi-day" ? 2 : 1}
        onDateChange={setCurrentDate}
      />
    </div>
  );
};

type CanvasPanelProps = CanvasPanelChromeProps & {
  /** Hide the narrow-screen day strip when the current route is showing another tab. */
  showDayStrip?: boolean;
  /**
   * Calendar owns this when route-level code needs the surface's `selection` too
   * (link interception + implicit calendar context). Other callers let the panel
   * manage it internally.
   */
  surface?: CanvasCalendarSurface;
} & Omit<CanvasHostProps, keyof CanvasCalendarSurfaceHostProps>;

const CanvasPanelFrame = ({
  leading,
  accountSwitcher,
  showDayStrip = true,
  surfaceHostProps,
  ...canvasHostProps
}: CanvasPanelChromeProps & {
  showDayStrip?: boolean;
  surfaceHostProps: CanvasCalendarSurfaceHostProps;
} & CanvasHostProps) => (
  <SplitPanelFrame
    header={<CanvasCalendarHeader leading={leading} accountSwitcher={accountSwitcher} />}
  >
    <CanvasCalendarDayStrip visible={showDayStrip} />
    <CanvasHost {...surfaceHostProps} {...canvasHostProps} />
  </SplitPanelFrame>
);

const CanvasPanelWithInternalSurface = (props: Omit<CanvasPanelProps, "surface">) => {
  const { hostProps } = useCanvasCalendarSurface();
  return <CanvasPanelFrame {...props} surfaceHostProps={hostProps} />;
};

/**
 * The shared canvas column: a full-height {@link SplitPanelFrame} with the unified
 * {@link CanvasCalendarHeader} on top and the shared {@link CanvasHost} body
 * below. By default it owns the calendar surface wiring internally; callers only
 * pass a `surface` when they need that same state elsewhere on the route.
 */
export const CanvasPanel = ({ surface, ...props }: CanvasPanelProps) => {
  if (surface) {
    return <CanvasPanelFrame {...props} surfaceHostProps={surface.hostProps} />;
  }

  return <CanvasPanelWithInternalSurface {...props} />;
};
